import { Fragment, type FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { IconChevronRightSmall } from '~/ui/core/icons';

import { useOptionalUrlTiles, useUrlTileEscapeClose } from '../../../url-tiles';
import { type AutomationTileTask, useAutomationTileTask } from '../api/use-automation-tile-task';
import { ProfileTaskBreadcrumbs } from '../tile/ProfileTaskBreadcrumbs';
import { TaskHeading } from '../tile/profile-task-tile/TaskHeading';
import { TaskTileError, TaskTileLoading } from '../tile/profile-task-tile/TaskTileFallbacks';
import { TaskTileFrame } from '../tile/profile-task-tile/TaskTileFrame';
import { AutomationAiNotice, AutomationScore } from './AutomationAssessmentSection';
import type { AutomationAssessment, StepAssessment } from './automation-assessment-model';
import { toPercent, verificationRetention } from './automation-assessment-scoring';
import type { AutomationAssessmentTilePayload } from './automation-assessment-tile-url';
import { automationAssessmentTranslationKeys as keys } from './automation-assessment-translations';
import { createAutomationStepTilePayload } from './automation-step-tile-url';
import { toProfileContext } from './automation-tile-address';

const FactorRow: FunctionComponent<{ readonly label: TranslationKey; readonly value: string }> = ({ label, value }) => {
    const translate = useTranslate();

    return (
        <div className="contents">
            <dt className="text-body-xs text-text-muted">{translate(label)}</dt>
            <dd className="m-0 text-right text-body-xs tabular-nums text-text-primary">{value}</dd>
        </div>
    );
};

const StepCard: FunctionComponent<{
    readonly index: number;
    readonly onOpen: (() => void) | undefined;
    readonly step: StepAssessment;
    readonly title: string;
}> = ({ index, onOpen, step, title }) => {
    const translate = useTranslate();
    const percent = (value: number): string => translate(keys.percent, { value });
    const reviewPercent = toPercent(verificationRetention(step.errorCost, step.mandatedHumanInvolvement));
    const peoplePercent = step.coreInteractionShare + step.coordinationShare;
    const body = (
        <Fragment>
            <div className="flex gap-m">
                <span
                    aria-hidden={true}
                    className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border-soft bg-surface-raised text-body-xs font-medium tabular-nums text-text-secondary"
                >
                    {index + 1}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-xxs">
                    <span className="text-body-s font-medium leading-normal text-text-primary">{title}</span>
                    <span className="text-body-xs text-text-content-faint">
                        {translate(keys.effortShare)}: {percent(toPercent(step.normalizedEffortShare))}
                    </span>
                </div>
                <span className="flex shrink-0 items-start gap-xs text-body-xs tabular-nums text-text-secondary">
                    {percent(toPercent(step.score))}
                    {onOpen === undefined ? null : (
                        <IconChevronRightSmall className="mt-px text-text-content-faint" size={14} />
                    )}
                </span>
            </div>
            <dl className="m-0 grid grid-cols-[1fr_auto] gap-x-l gap-y-xs pl-[calc(var(--spacing-m)+1.5rem)]">
                <FactorRow label={keys.digitalShare} value={percent(step.digitalShare)} />
                <FactorRow label={keys.people} value={percent(peoplePercent)} />
                <FactorRow label={keys.capability} value={percent(toPercent(step.capability))} />
                <FactorRow label={keys.passesWithoutReview} value={percent(100 - reviewPercent)} />
                <FactorRow
                    label={keys.mandated}
                    value={translate(step.mandatedHumanInvolvement ? keys.yes : keys.no)}
                />
            </dl>
            <p className="m-0 pl-[calc(var(--spacing-m)+1.5rem)] text-xs leading-normal text-text-secondary">
                {step.rationale}
            </p>
        </Fragment>
    );
    const cardClass = 'flex w-full flex-col gap-m rounded-xl bg-surface-subtle px-xl py-xl text-left';

    return (
        <li className="list-none">
            {onOpen === undefined ? (
                <div className={cardClass}>{body}</div>
            ) : (
                <button
                    className={`${cardClass} m-0 cursor-pointer appearance-none border-none transition-colors duration-100 ease-in-out hover:bg-surface-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`}
                    data-testid="automation-assessment-step-card"
                    type="button"
                    onClick={onOpen}
                >
                    {body}
                    <span className="sr-only">{translate(keys.openStep)}</span>
                </button>
            )}
        </li>
    );
};

const AssessmentDetails: FunctionComponent<{
    readonly assessment: AutomationAssessment;
    readonly onOpenStep: ((workflowStepId: string) => void) | undefined;
    readonly task: AutomationTileTask;
}> = ({ assessment, onOpenStep, task }) => {
    const translate = useTranslate();
    const titles = new Map(task.workflowSteps.map(({ title, workflowStepId }) => [workflowStepId, title]));

    return (
        <div className="flex flex-col gap-3xl" data-testid="automation-assessment-details">
            <div className="flex gap-xl rounded-xl bg-surface-subtle px-xl py-xl">
                <p className="m-0 min-w-0 flex-1 text-xs leading-normal text-text-secondary">{assessment.summary}</p>
                <AutomationScore fraction={assessment.taskScore} size="l" />
            </div>
            <ol className="m-0 flex list-none flex-col gap-s p-0">
                {assessment.steps.map((step, index) => (
                    <StepCard
                        key={step.workflowStepId}
                        index={index}
                        onOpen={onOpenStep === undefined ? undefined : () => onOpenStep(step.workflowStepId)}
                        step={step}
                        title={titles.get(step.workflowStepId) ?? step.workflowStepId}
                    />
                ))}
            </ol>
            <div className="flex flex-col gap-s">
                <p className="m-0 text-body-xs leading-normal text-text-content-faint">
                    {translate(keys.formula, { method: assessment.methodVersion })}
                </p>
                <AutomationAiNotice />
            </div>
        </div>
    );
};

export const AutomationAssessmentTile: FunctionComponent<{
    readonly onClose: () => void;
    readonly payload: AutomationAssessmentTilePayload;
}> = ({ onClose, payload }) => {
    const translate = useTranslate();
    useUrlTileEscapeClose(onClose);
    const urlTiles = useOptionalUrlTiles();
    const context = toProfileContext(payload);
    const query = useAutomationTileTask({
        context,
        language: payload.language,
        taskId: payload.taskId,
        taskVersionId: payload.taskVersionId,
    });

    if (query.isLoading) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileLoading />
            </TaskTileFrame>
        );
    }

    if (query.isError || query.data === undefined) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileError />
            </TaskTileFrame>
        );
    }

    const task = query.data;
    const assessment = task.assessment;

    return (
        <TaskTileFrame testId="automation-assessment-tile" onClose={onClose}>
            <div className="flex flex-col gap-m">
                {context === undefined ? null : (
                    <ProfileTaskBreadcrumbs context={context} currentItemTitle={translate(keys.detailBreadcrumb)} />
                )}
                <TaskHeading title={translate(keys.detailTitle)} />
                <p className="m-0 text-body-s text-text-secondary">{task.title}</p>
            </div>
            {assessment === undefined ? (
                <EmptyStateMessage>{translate(keys.notAssessed)}</EmptyStateMessage>
            ) : (
                <AssessmentDetails
                    assessment={assessment}
                    task={task}
                    onOpenStep={
                        urlTiles === null
                            ? undefined
                            : (workflowStepId) =>
                                  urlTiles.openTile(
                                      createAutomationStepTilePayload({
                                          context,
                                          language: payload.language,
                                          taskId: payload.taskId,
                                          taskVersionId: payload.taskVersionId,
                                          workflowStepId,
                                      }),
                                  )
                    }
                />
            )}
        </TaskTileFrame>
    );
};
